import { useMemo, useCallback } from 'react';

export interface UseTableSelectionProps<T> {
  rows: T[];
  selectedRows?: Set<string | number>;
  onSelectionChange?: (selectedIds: Set<string | number>) => void;
  getRowId?: (row: T) => string | number;
}

export interface UseTableSelectionReturn<T> {
  selectedRowIds: Set<string | number>;
  isAllSelected: boolean;
  isIndeterminate: boolean;
  getRowIdentifier: (row: T) => string | number;
  handleSelectAll: (checked: boolean) => void;
  handleRowSelect: (row: T, checked: boolean) => void;
}

/**
 * Custom hook for managing table selection state
 *
 * Features:
 * - Manages selected row IDs
 * - Handles select all functionality with indeterminate state
 * - Provides callbacks for row selection
 * - Supports custom row ID extraction
 * - Optimized with useMemo and useCallback
 */
export const useTableSelection = <T>({
  rows,
  selectedRows,
  onSelectionChange,
  getRowId,
}: UseTableSelectionProps<T>): UseTableSelectionReturn<T> => {
  /**
   * Helper function to get row ID
   */
  const getRowIdentifier = useCallback(
    (row: T) => {
      return getRowId
        ? getRowId(row)
        : ((row as Record<string, unknown>).id as string | number);
    },
    [getRowId]
  );

  /**
   * Selection state management
   */
  const selectedRowIds = useMemo(
    () => selectedRows || new Set<string | number>(),
    [selectedRows]
  );

  const isAllSelected = useMemo(() => {
    if (rows.length === 0) return false;
    return rows.every((row) => selectedRowIds.has(getRowIdentifier(row)));
  }, [rows, selectedRowIds, getRowIdentifier]);

  const isIndeterminate = selectedRowIds.size > 0 && !isAllSelected;

  /**
   * Handles select all checkbox toggle
   */
  const handleSelectAll = useCallback(
    (checked: boolean) => {
      if (!onSelectionChange) return;

      if (checked) {
        const allIds = new Set(rows.map(getRowIdentifier));
        onSelectionChange(allIds);
      } else {
        onSelectionChange(new Set());
      }
    },
    [rows, onSelectionChange, getRowIdentifier]
  );

  /**
   * Handles individual row selection toggle
   */
  const handleRowSelect = useCallback(
    (row: T, checked: boolean) => {
      if (!onSelectionChange) return;

      const rowId = getRowIdentifier(row);
      const newSelection = new Set(selectedRowIds);

      if (checked) {
        newSelection.add(rowId);
      } else {
        newSelection.delete(rowId);
      }

      onSelectionChange(newSelection);
    },
    [selectedRowIds, onSelectionChange, getRowIdentifier]
  );

  return {
    selectedRowIds,
    isAllSelected,
    isIndeterminate,
    getRowIdentifier,
    handleSelectAll,
    handleRowSelect,
  };
};
